import { FC, Fragment, useMemo, useState } from "react"
import cx from "classnames"
import type { SequenceAPIResponse } from "@/types/entities/sequence"
import OutlineButton from "@/components/OutlineButton"
import EmailAccordionCard from "@/components/EmailAccordionCard"
import CampaignsTable from "../CampaignsTable"
import SequencePromptsPanel from "../SequencePromptsPanel"
import SequenceSpamTestPanel from "../SequenceSpamTestPanel"

type Props = {
  className?: string
  selectedSequence: SequenceAPIResponse
}

type SequenceViewTabs = "Messaging" | "Prompts" | "Spam Test" | "Campaigns"

const showCampaignsTab = false

const SequenceView: FC<Props> = ({
  className,
  selectedSequence: sequence,
}: Props) => {
  const [activeView, setActiveView] = useState<SequenceViewTabs>("Messaging")
  const tabs = useMemo(() => {
    return [
      {
        label: "Messaging",
        value: "Messaging" as const,
      },
      {
        label: "Prompts",
        value: "Prompts" as const,
      },
      ...(showCampaignsTab
        ? [
            {
              label: `Campaigns (${sequence?.campaigns.length})`,
              value: "Campaigns" as const,
            },
          ]
        : []),
      {
        label: "Spam Test",
        value: "Spam Test" as const,
      },
    ]
  }, [sequence])
  const updateActiveTab = (type: SequenceViewTabs) => {
    return () => {
      setActiveView(type)
    }
  }
  const emails = sequence?.emails

  return (
    <div className={cx("p-6 flex flex-col gap-5", className)}>
      <div
        className={cx("p-1 rounded-[9px] bg-input w-full flex [&>*]:flex-1")}
      >
        {tabs.map(({ label, value }, i) => (
          <Fragment key={i}>
            {activeView === value ? (
              <OutlineButton
                onClick={updateActiveTab(value as SequenceViewTabs)}
                className="btn-xs hover:bg-white !min-h-[34px] !h-auto text-sm bg-white !text-primary outline-outline outline outline-1 border-none shadow-[0px_1px_2px_0px_rgba(0,0,0,0.04)]"
                data-testid={`sequence-tab-${value
                  .toLowerCase()
                  .replace(/\s+/g, "-")}`}
              >
                {label}
              </OutlineButton>
            ) : (
              <OutlineButton
                onClick={updateActiveTab(value as SequenceViewTabs)}
                className="btn-xs !min-h-[34px] !h-auto text-sm bg-transparent border-none text-black"
                data-testid={`sequence-tab-${value
                  .toLowerCase()
                  .replace(/\s+/g, "-")}`}
              >
                {label}
              </OutlineButton>
            )}
          </Fragment>
        ))}
      </div>
      {activeView === "Messaging" && (
        <div className="flex flex-col gap-5">
          {emails.map((email, emailIndex) => (
            <EmailAccordionCard
              key={emailIndex}
              email={email}
              emailIndex={emailIndex}
            />
          ))}
        </div>
      )}
      {activeView === "Campaigns" && (
        <CampaignsTable campaigns={sequence.campaigns} />
      )}
      {activeView === "Prompts" && <SequencePromptsPanel sequence={sequence} />}
      {activeView === "Spam Test" && (
        <SequenceSpamTestPanel sequence={sequence} />
      )}
    </div>
  )
}

export default SequenceView
